import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import styles from "./AppoinmentStyle.module.css";
import endpoints from "../api/endpoints";

export default function Feedback() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    rating: 0,
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const accentColor = '#c6922b';
console.log('userData', userData, token);

  // Fetch user data using token
  useEffect(() => {
    const fetchUserData = async () => {
      if (!token) return;

      try {
        setLoading(true);
        setError(null);
        const response = await axios.get(endpoints.feedback.getUser(token));
        setUserData(response.data);
        console.log('User data:', response.data);

        // Pre-fill form with user data if available
        if (response.data) {
          setFormData(prev => ({
            ...prev,
            name: response.data.name || response.data.first_name || '',
            email: response.data.email || ''
          }));
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        token: token,
        feedback: formData.message,
        rating: formData.rating
      };

      await axios.post(endpoints.feedback.submit, payload);
      console.log('Feedback submitted:', payload);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className={styles.container} style={{ '--accent': accentColor }}>
        <div className={styles.feedbackPage}>
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{
              width: '48px',
              height: '48px',
              border: '4px solid #f3f3f3',
              borderTop: `4px solid ${accentColor}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
            <p style={{ color: '#666' }}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className={styles.container} style={{ '--accent': accentColor }}>
        <div className={styles.feedbackPage}>
          <div className={styles.feedbackSuccess}>
            <div className={styles.successIcon}>
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2 className={styles.successTitle}>Thank You!</h2>
            <p className={styles.successMessage}>
              Your feedback has been submitted successfully. We appreciate you taking the time to help us improve.
            </p>
            <button
              className={styles.feedbackBtn}
              onClick={() => {
                setSubmitted(false);
                setFormData({ name: '', email: '', rating: 0, message: '' });
              }}
            >
              Submit Another Feedback
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container} style={{ '--accent': accentColor }}>
      <div className={styles.feedbackPage}>
        <div className={styles.feedbackHeader}>
          <h1 className={styles.feedbackTitle}>
            We Value Your <span style={{ color: accentColor }}>Feedback</span>
          </h1>
          <p className={styles.feedbackSubtitle}>
            Help us improve our services by sharing your experience
          </p>
        </div>

        {error && (
          <div style={{
            padding: '12px 16px',
            background: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: '8px',
            color: '#ff4d4f',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            {error}
          </div>
        )}

        <form className={styles.feedbackForm} onSubmit={handleSubmit}>
          <div className={styles.feedbackField}>
            <label className={styles.feedbackLabel}>Your Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              className={styles.feedbackInput}
              placeholder="Enter your name"
              readOnly
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>

          <div className={styles.feedbackField}>
            <label className={styles.feedbackLabel}>Email Address</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              className={styles.feedbackInput}
              placeholder="Enter your email"
              readOnly
              style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
            />
          </div>

          <div className={styles.feedbackField}>
            <label className={styles.feedbackLabel}>How would you rate your experience?</label>
            <div className={styles.ratingContainer}>
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={styles.ratingBtn}
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(0)}
                >
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill={star <= (hoveredRating || formData.rating) ? accentColor : 'none'}
                    stroke={accentColor}
                    strokeWidth="2"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </button>
              ))}
              <span className={styles.ratingText}>
                {formData.rating > 0 ? `${formData.rating} out of 5` : 'Select a rating'}
              </span>
            </div>
          </div>

          <div className={styles.feedbackField}>
            <label className={styles.feedbackLabel}>Your Feedback</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              className={styles.feedbackTextarea}
              placeholder="Tell us about your experience..."
              rows={5}
              required
            />
          </div>

          <button
            type="submit"
            className={styles.feedbackBtn}
            disabled={!formData.rating || submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>
      </div>
    </div>
  );
}
